/**
 * Content for /enterprise/dgx-spark-tender-malaysia/ — RFQ and tender support
 * for the DGX Spark / GB10 family, across every OEM build.
 *
 * WHO THIS IS FOR (Jazz, 2026-09-12): a government or GLC procurement officer
 * writing an RFQ, and an integrator's tender team assembling a bid. NOT the
 * desk-side buyer — that is the hub page. This reader has a document to produce
 * and a deadline, and what they need is a spec block they can lift, a budget
 * figure their approver will accept, and certainty that the bid will not be
 * disqualified on authorisation.
 *
 * WHY IT EXISTS. The live MY SERP for "dgx spark tender rfq malaysia" already
 * cites Anchor Sprint three times in the AI Overview — including under "Certified
 * OEM Build Options" — with only two other sellers alongside. We are in the
 * answer and had no page to send that reader to. This is that page.
 *
 * THE HOOK, and it comes from Google's own Overview: "to avoid single-brand locks
 * in public procurement, structure your tender to accept equivalent ecosystem
 * builds." A public tender cannot lawfully name one brand. It must admit
 * equivalents — and the compute here is identical across every badge, which makes
 * an equivalence clause unusually easy to write. We supply all of them. Nobody
 * else publishes that table.
 *
 * ===================== ACCURACY GATES — READ BEFORE EDITING =====================
 *
 * 1. NO MOF CLAIM. Anchor Sprint is NOT MOF-registered (Jazz, 2026-09-12). Google's
 *    Overview says government and public-university tenders usually require MOF
 *    registration under ICT supply codes. We therefore DO NOT claim it, DO NOT imply
 *    it, and DO NOT quote those codes as if they were ours. The page is honest about
 *    being the supply and authorisation partner behind a registered bidder. Adding an
 *    MOF claim here without a certificate would be a false statement on a
 *    government-facing page — the worst place on the site to get one wrong.
 *
 * 2. LOA IS PER-TENDER, NOT STANDING. We obtain the OEM Letter of Authorisation for
 *    a specific tender on request. We do NOT hold standing LOAs on file for every
 *    brand. Never write "we hold LOA from all OEMs" — a bidder disqualified on a
 *    letter we could not produce is a serious commercial and reputational failure.
 *
 * 3. ONE WORKING DAY, not 24 hours. Excludes weekends and public holidays, and
 *    matches the "two working days" language used elsewhere on the site.
 *
 * 4. Prices are the PUBLIC RETAIL REFERENCE from dgxSparkMarketPrice — a dated
 *    market figure, never our quotation. Same rule as the hub page.
 *
 * 5. NO SELLER IS EVER NAMED — not the distributor, and not a competitor.
 *    Sourcing wording stays "authorised distribution". No stock claims, no
 *    delivery lead times. Same rule that removed a retailer attribution from the
 *    hub page in #369: publish the fact, anonymise the seller. It holds in these
 *    comments too. The build strips comments, so a name here is not a public
 *    leak — but it sits waiting to be lifted into page copy, which is exactly
 *    how it shipped last time. The count ("two other sellers") carries the
 *    competitive point on its own; the names never did any work.
 */

import { dgxSparkMarketPrice } from "./dgx-spark";

export interface TenderSpecRow {
  /** Wording a procurement officer can lift into a tender document verbatim. */
  requirement: string;
  baseline: string;
}

/**
 * The copy-paste block. Phrased as minimum requirements rather than as a product
 * description, because that is the register a tender document is written in.
 * Figures are NVIDIA's own, locked across every OEM build — see dgxSparkSpecs.
 */
export const tenderSpecRows: TenderSpecRow[] = [
  { requirement: "Processor architecture", baseline: "NVIDIA GB10 Grace Blackwell Superchip — 20-core Arm CPU (10× Cortex-X925, 10× Cortex-A725) with integrated Blackwell GPU" },
  { requirement: "AI compute", baseline: "Minimum 1 petaFLOP at FP4 precision (1,000 TOPS)" },
  { requirement: "System memory", baseline: "128 GB LPDDR5x unified, coherent across CPU and GPU" },
  { requirement: "Memory bandwidth", baseline: "Minimum 273 GB/s" },
  { requirement: "Networking", baseline: "NVIDIA ConnectX-7 at 200 Gbps (2× QSFP) plus onboard 10 GbE RJ-45" },
  { requirement: "Storage", baseline: "Self-encrypting NVMe M.2 SSD, 1 TB to 4 TB as specified" },
  { requirement: "Operating environment", baseline: "NVIDIA DGX OS with the NVIDIA AI Enterprise software stack" },
  { requirement: "Power", baseline: "240 W PSU, single standard outlet — no rack or facility power works required" },
  { requirement: "Form factor", baseline: "Desktop, 150 × 150 × 50.5 mm, 1.2 kg" },
  { requirement: "Scaling", baseline: "Two units linkable over ConnectX-7 for models up to 405 billion parameters" },
];

/**
 * The equivalence table — the reason this page exists.
 *
 * `note` must describe a REAL difference or say plainly that there is none. The
 * temptation on a tender page is to inflate differentiation; the honest and more
 * useful fact is that the compute does not vary, which is exactly what makes an
 * equivalence clause defensible.
 */
export const tenderOemBuilds = [
  { brand: "NVIDIA", model: "DGX Spark (Founders Edition)", page: "", note: "The reference build. 4 TB self-encrypting NVMe." },
  { brand: "ASUS", model: "Ascent GX10", page: "asus-ascent-gx10-malaysia", note: "Widest published capacity ladder — 1 TB, 2 TB and 4 TB." },
  { brand: "Dell Technologies", model: "Pro Max with GB10", page: "dell-pro-max-gb10-malaysia", note: "Slots into an existing Dell estate's support and asset process." },
  { brand: "HP", model: "ZGX Nano AI Station (G1n)", page: "hp-zgx-nano-malaysia", note: "Z-series workstation line; a radio-free option exists for restricted sites." },
  { brand: "Lenovo", model: "ThinkStation PGX", page: "lenovo-thinkstation-pgx-malaysia", note: "Documents TPM 2.0, UEFI Secure Boot and self-encrypting drives — the items a security review asks about." },
  { brand: "Acer", model: "Veriton GN100", page: "acer-veriton-gn100-malaysia", note: "Acer's build of the same platform." },
  { brand: "Gigabyte", model: "AI TOP ATOM", page: "", note: "Offered at several storage tiers." },
  { brand: "MSI", model: "EdgeXpert (MS-C931)", page: "", note: "Sold into industrial and edge deployments as well as the desk." },
];

/** What we actually do for a tender team, in the order they need it. */
export const tenderSupport = [
  {
    title: "Specification, written to be lifted",
    detail:
      "The baseline table below is phrased as tender requirements, not marketing copy. Take it into your document as it stands, or send us your draft and we will mark where a clause would unintentionally exclude a compliant build.",
  },
  {
    title: "An equivalence clause that survives review",
    detail:
      "Public procurement cannot lawfully specify a single brand. Because NVIDIA fixes the superchip, the 128 GB of unified memory and the interconnect across every OEM build, an 'or equivalent' clause here is unusually clean to write — the compute genuinely does not vary. We will tell you which parameters are safe to mandate and which will narrow the field to one bidder.",
  },
  {
    title: "OEM Letter of Authorisation, obtained for your tender",
    detail:
      "We obtain the OEM Letter of Authorisation for the specific tender on request, across every DGX Spark build — NVIDIA, ASUS, Dell, HP, Lenovo, Acer, Gigabyte and MSI. Tell us the tender reference and closing date and we start the request the same day.",
  },
  {
    title: "Indicative budget figures for approval",
    detail:
      "A budget paper needs a number before a tender exists. The public retail range below is enough to get an approval moving; a formal quotation in Ringgit follows, with SST treatment stated and a MyInvois e-invoice on purchase.",
  },
  {
    title: "Quotation in one working day",
    detail:
      "Send the configuration, quantity and closing date and we return a formal quotation within one working day. Tender calendars do not move, so ours does not either.",
  },
];

/**
 * Honest scope statement. We are NOT MOF-registered — see accuracy gate 1. Saying
 * so plainly is better than silence: a procurement officer who discovers it later
 * has wasted their time and ours, and a registered integrator reading this is
 * exactly the partner we want.
 */
export const tenderScope =
  "A note on who we are in your process. Anchor Sprint supplies through authorised Malaysian distribution and is not a Ministry of Finance registered vendor, so where a tender requires MOF registration under the ICT supply codes we are the supply and authorisation partner behind a registered bidder rather than the bidder itself. If you are an integrator who holds that registration, this is the arrangement we are set up for. If you are a procurement officer, tell us early and we will say plainly whether we can serve the tender directly or should sit behind someone who can.";

export const tenderPriceNote =
  `For budget purposes the DGX Spark family is listed publicly at Malaysian retail between roughly ${dgxSparkMarketPrice.low} and ${dgxSparkMarketPrice.high}, depending on OEM build and storage tier (${dgxSparkMarketPrice.source}, ${dgxSparkMarketPrice.retrieved}). Storage capacity moves the number far more than the badge does. These are public retail figures quoted for budgeting, not our quotation — a tendered price depends on quantity, configuration, warranty term and the support scope you specify.`;

export const tenderFaqs = [
  {
    question: "Can you support a government tender for NVIDIA DGX Spark in Malaysia?",
    answer:
      "We support the technical and supply side of it: the specification, the equivalence clause, the OEM Letter of Authorisation and the quotation. Anchor Sprint is not a Ministry of Finance registered vendor, so where a tender requires MOF registration we work behind a registered bidder rather than submitting ourselves. Tell us the tender reference and we will say plainly which of the two applies.",
  },
  {
    question: "Do you provide an OEM Letter of Authorisation for a DGX Spark tender?",
    answer:
      "Yes, obtained for the specific tender on request, across every DGX Spark build — NVIDIA Founders Edition, ASUS Ascent GX10, Dell Pro Max with GB10, HP ZGX Nano, Lenovo ThinkStation PGX, Acer Veriton GN100, Gigabyte AI TOP ATOM and MSI EdgeXpert. Send the tender reference and the closing date and we begin the request the same day. LOAs are issued per tender rather than held standing, which is normal for this class of hardware.",
  },
  {
    question: "How do I write a DGX Spark tender specification without locking it to one brand?",
    answer:
      "Specify the platform, not the badge. NVIDIA fixes the GB10 Grace Blackwell superchip, the 128 GB of coherent unified memory, the 273 GB/s bandwidth and the ConnectX-7 interconnect across every OEM build, so those parameters are safe to mandate and will still admit eight compliant bidders. What narrows the field is specifying storage capacity, chassis dimensions, warranty terms or management features — legitimate if you need them, but state them knowingly. Send us the draft and we will mark the clauses that would reduce it to a single supplier.",
  },
  {
    question: "How much should we budget per DGX Spark unit in a tender?",
    answer:
      `${tenderPriceNote} For a multi-unit tender, tell us the quantity and we will return a figure in Ringgit within one working day.`,
  },
  {
    question: "How quickly can you turn around an RFQ?",
    answer:
      "One working day for a formal quotation, from the point we have the configuration, quantity and closing date. If your tender needs a signed OEM authorisation as well, say so at the same time — that runs in parallel rather than after.",
  },
  {
    question: "Which DGX Spark OEM build should a tender specify?",
    answer:
      "Usually whichever your existing support and procurement arrangements already cover, because the compute is identical. Where a security review is involved, Lenovo's ThinkStation PGX publishes TPM 2.0, UEFI Secure Boot and self-encrypting storage, which shortens that conversation. Where capacity choice matters most, ASUS publishes the widest storage ladder. We quote all eight, so the recommendation does not change what we sell.",
  },
];
